<template>
    <div>
        <el-form :inline="true" @submit.native.prevent>
            <el-form-item label="任务标题">
                <el-input v-model="title" placeholder="默认为文件名" size="small" style="width: 240px"
                          :disabled="uploading"></el-input>
            </el-form-item>
            <el-form-item>
                <input type="file" ref="file" :accept="accepts" style="display:none" @change="handlePicked">
                <el-button size="small" icon="el-icon-video-camera" :disabled="uploading"
                           @click="$refs.file.click()">选择视频
                </el-button>
                <span v-if="file" style="margin-left: 8px">
                    {{ file.name }} ({{ (file.size / 1048576).toFixed(1) }} MB)
                </span>
            </el-form-item>
            <el-form-item>
                <el-button type="primary" size="small" :disabled="!file || uploading" @click="start">
                    开始上传并分析
                </el-button>
            </el-form-item>
        </el-form>
        <template v-if="uploading || phase === 'error'">
            <el-progress :percentage="percent" :status="phase === 'error' ? 'exception' : null"></el-progress>
            <div style="font-size: 12px; color: #909399">{{ phaseText }}</div>
        </template>
    </div>
</template>

<script>
    import SparkMD5 from 'spark-md5';
    import axios from 'axios';
    import api from '../config/api';

    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB/片,低于 nginx client_max_body_size 100M

    export default {
        name: 'video-chunk-upload',
        data() {
            return {
                file: null,
                title: '',
                uploading: false,
                percent: 0,
                phase: '', // md5 | upload | error
                extensions: ['mp4', 'mov', 'avi', 'flv', 'mkv', 'm4v', 'wmv', 'mpeg', 'mpg', '3gp', 'ogg'],
            };
        },
        computed: {
            accepts() {
                return this.extensions.map(e => '.' + e).join(',');
            },
            phaseText() {
                if (this.phase === 'md5') return '正在计算文件校验值…';
                if (this.phase === 'upload') return '切片上传中 ' + this.percent + '%';
                if (this.phase === 'error') return '上传失败,请重新选择文件或重试';
                return '';
            }
        },
        methods: {
            handlePicked(e) {
                const f = e.target.files[0];
                e.target.value = '';
                if (!f) return;
                const ext = f.name.split('.').pop().toLowerCase();
                if (this.extensions.indexOf(ext) === -1) {
                    this.$message.error('不支持的视频格式: ' + ext);
                    return;
                }
                if (!f.size) {
                    this.$message.error('文件为空');
                    return;
                }
                if (f.size > 4 * 1024 * 1024 * 1024) {
                    this.$message.error('文件超过 4GB 上限');
                    return;
                }
                this.file = f;
                this.phase = ''; // 清除上一次的错误态
            },
            makeUuid() {
                return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
                    const r = Math.random() * 16 | 0;
                    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
                });
            },
            async computeMd5() {
                this.phase = 'md5';
                const spark = new SparkMD5.ArrayBuffer();
                for (let off = 0; off < this.file.size; off += CHUNK_SIZE) {
                    const buf = await this.file.slice(off, off + CHUNK_SIZE).arrayBuffer();
                    spark.append(buf);
                    this.percent = Math.min(99, Math.floor((off + CHUNK_SIZE) / this.file.size * 100));
                }
                return spark.end();
            },
            postChunk(form) {
                const token = this.$mixToken(this.$store.getters['user/token']);
                return axios.post(api.url + '/admin/video-analysis/upload-chunk', form,
                    {headers: {'user-token': token, 'Accept': 'application/json'}, timeout: 120000});
            },
            async start() {
                this.uploading = true;
                this.percent = 0;
                try {
                    const md5 = await this.computeMd5();
                    this.phase = 'upload';
                    this.percent = 0;
                    const uuid = this.makeUuid();
                    const total = Math.ceil(this.file.size / CHUNK_SIZE);
                    let taskId = null;
                    for (let seq = 1; seq <= total; seq++) {
                        const blob = this.file.slice((seq - 1) * CHUNK_SIZE, seq * CHUNK_SIZE);
                        const form = new FormData();
                        form.append('uuid', uuid);
                        form.append('seq', seq);
                        form.append('total', total);
                        form.append('filename', this.file.name);
                        form.append('filesize', this.file.size);
                        form.append('md5', md5);
                        form.append('title', this.title);
                        form.append('chunk', blob, 'chunk' + seq);

                        let ok = false, lastError = null;
                        for (let attempt = 0; attempt < 3 && !ok; attempt++) {
                            try {
                                const res = await this.postChunk(form);
                                if (!res.data.success) throw new Error(res.data.text);
                                if (res.data.data && res.data.data.task_id) taskId = res.data.data.task_id;
                                ok = true;
                            } catch (err) {
                                lastError = err;
                            }
                        }
                        if (!ok) throw lastError;
                        this.percent = Math.floor(seq / total * 100);
                    }
                    this.$message.success('上传完成(任务 #' + taskId + '),已加入分析队列');
                    this.file = null;
                    this.title = '';
                    this.$emit('uploaded');
                } catch (err) {
                    this.phase = 'error';
                    this.$message.error('上传失败: ' + (err && err.message ? err.message : err));
                } finally {
                    if (this.phase !== 'error') this.phase = '';
                    this.uploading = false;
                }
            }
        }
    };
</script>
